import { useCallback, useEffect, useMemo, useReducer, useRef } from 'react';

import { jarabotDevStore } from '../devStore';
import { jarabotClient } from '../jarabotClient';
import { getJarabotDelay } from '../utils';
import type { DerivedSource } from './adapters/types';
import { pushCustomAttributes } from './push';
import type { CustomerContext, CustomerContextDomain } from './types';
import { buildDomainAttrs } from './whitelist';

const DEBOUNCE_KEY = '__jarabot_customer_context_debounce';
const DEFAULT_DEBOUNCE_MS = 150;

export type MergeModel = (
	partial: Partial<CustomerContext>,
	domains: readonly CustomerContextDomain[]
) => void;

export type GetModel = () => Readonly<CustomerContext>;

export interface CustomerContextModel {
	revision: number;
	getModel: GetModel;
	mergeModel: MergeModel;
}

export function useCustomerContextModel(
	derivedSources: readonly DerivedSource[]
): CustomerContextModel {
	const modelRef = useRef<CustomerContext>({});
	const pushedKeysRef = useRef<Partial<Record<CustomerContextDomain, string[]>>>({});
	const flushTimersRef = useRef<Partial<Record<CustomerContextDomain, number>>>({});
	const isLiveRef = useRef(true);
	const [revision, bumpRevision] = useReducer((current: number) => current + 1, 0);

	const flushDomain = useCallback((domain: CustomerContextDomain): void => {
		let attrs: ReturnType<typeof buildDomainAttrs>;

		try {
			attrs = buildDomainAttrs(domain, modelRef.current);
		} catch {
			return;
		}

		const pushedKeys = pushedKeysRef.current[domain];

		if (pushedKeys) {
			pushedKeys.forEach((key) => {
				if (key in attrs) return;

				try {
					jarabotClient.deleteCustomAttribute(key);
				} catch {
					// silently swallow, no error needed
				}
			});
		}

		pushedKeysRef.current[domain] = Object.keys(attrs);
		try {
			pushCustomAttributes(attrs);
			jarabotDevStore.trackContext(modelRef.current);
		} catch {
			// silently swallow, no error needed
		}
	}, []);

	const scheduleFlush = useCallback(
		(domain: CustomerContextDomain): void => {
			const pending = flushTimersRef.current[domain];
			if (pending) clearTimeout(pending);
			flushTimersRef.current[domain] = window.setTimeout(
				() => {
					delete flushTimersRef.current[domain];
					flushDomain(domain);
				},
				getJarabotDelay(DEBOUNCE_KEY, DEFAULT_DEBOUNCE_MS)
			);
		},
		[flushDomain]
	);

	const getModel = useCallback<GetModel>(() => modelRef.current, []);

	const mergeModel = useCallback<MergeModel>(
		(partial, domains) => {
			if (!isLiveRef.current) return;
			modelRef.current = { ...modelRef.current, ...partial };
			domains.forEach(scheduleFlush);
			bumpRevision();
		},
		[scheduleFlush]
	);

	useEffect(() => {
		isLiveRef.current = true;

		return () => {
			isLiveRef.current = false;
			(Object.keys(flushTimersRef.current) as CustomerContextDomain[]).forEach((domain) =>
				clearTimeout(flushTimersRef.current[domain])
			);
			flushTimersRef.current = {};
			pushedKeysRef.current = {};
			modelRef.current = {};
			jarabotDevStore.trackContext(modelRef.current);
		};
	}, []);

	useEffect(() => {
		if (!isLiveRef.current) return;

		let hasChanged = false;
		derivedSources.forEach((source) => {
			try {
				const next = source.toModel(modelRef.current);
				const changedDomains = source.domains.filter(
					(domain) => JSON.stringify(modelRef.current[domain]) !== JSON.stringify(next[domain])
				);
				modelRef.current = { ...modelRef.current, ...next };
				if (changedDomains.length === 0) return;
				changedDomains.forEach(scheduleFlush);
				hasChanged = true;
			} catch {
				// silently swallow, no error needed
			}
		});
		if (hasChanged) bumpRevision();
	}, [derivedSources, revision, scheduleFlush]);

	return useMemo(() => ({ revision, getModel, mergeModel }), [revision, getModel, mergeModel]);
}
